import React, { useEffect, useState } from 'react';
import '../styles/header.css';
import { initializeProfileMenu } from '../scripts/profileMenu';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
  confirmPasswordReset,
  startPasswordReset,
} from '../lib/authClient';
import {
  getResolvedTheme,
  getThemeSettings,
  saveThemeSettings,
  themeChangeEventName,
} from '../lib/themePreferences';

const EMPTY_LOGIN_FORM = { login: '', password: '' };
const EMPTY_REGISTER_FORM = { login: '', email: '', password: '' };
const EMPTY_PASSWORD_RESET_START_FORM = { loginOrEmail: '' };
const EMPTY_PASSWORD_RESET_CONFIRM_FORM = { email: '', code: '', newPassword: '' };
const BACK_ICON_SRC = '/icons/arrow_back_24dp_FFFFFF.svg';
const SETTINGS_ICON_SRC = '/icons/settings_24dp_FFFFFF.svg';

const ThemeControls = () => {
  const [themeSettings, setThemeSettings] = useState(() => getThemeSettings());
  const currentThemeLabel = getResolvedTheme(themeSettings) === 'dark' ? 'тёмная' : 'светлая';

  useEffect(() => {
    const handleThemeChange = (event) => {
      setThemeSettings({
        mode: event.detail?.mode === 'dark' ? 'dark' : 'light',
        syncWithDevice: Boolean(event.detail?.syncWithDevice),
      });
    };

    window.addEventListener(themeChangeEventName, handleThemeChange);

    return () => {
      window.removeEventListener(themeChangeEventName, handleThemeChange);
    };
  }, []);

  const applyNextTheme = (nextSettings) => {
    const result = saveThemeSettings(nextSettings);
    setThemeSettings({
      mode: result.mode,
      syncWithDevice: result.syncWithDevice,
    });
  };

  return (
    <div className="profile-theme-block">
      <span className="profile-theme-label">Тема</span>
      <div className="profile-theme-switch">
        <button
          type="button"
          className={`profile-theme-btn ${themeSettings.syncWithDevice ? 'active' : ''}`}
          onClick={() => applyNextTheme({ mode: themeSettings.mode, syncWithDevice: true })}
        >
          Авто
        </button>
        <button
          type="button"
          className={`profile-theme-btn ${!themeSettings.syncWithDevice ? 'active' : ''}`}
          onClick={() => applyNextTheme({ mode: themeSettings.mode, syncWithDevice: false })}
        >
          Вручную
        </button>
      </div>
      <p className="profile-theme-note">
        {themeSettings.syncWithDevice
          ? `Сейчас сайт следует теме устройства: ${currentThemeLabel}.`
          : `Сейчас выбрана ${currentThemeLabel} тема. Переключить её можно кнопкой в шапке.`}
      </p>
    </div>
  );
};

const Profile = () => {
  const {
    user,
    isLoading,
    isSubmitting,
    isAuthenticated,
    signIn,
    signUp,
    signOut,
  } = useAuth();
  const { showToast } = useToast();
  const [authMode, setAuthMode] = useState('login');
  const [loginForm, setLoginForm] = useState(EMPTY_LOGIN_FORM);
  const [registerForm, setRegisterForm] = useState(EMPTY_REGISTER_FORM);
  const [passwordResetStartForm, setPasswordResetStartForm] = useState(EMPTY_PASSWORD_RESET_START_FORM);
  const [passwordResetConfirmForm, setPasswordResetConfirmForm] = useState(EMPTY_PASSWORD_RESET_CONFIRM_FORM);
  const [passwordResetStep, setPasswordResetStep] = useState('start');
  const [isPasswordResetSubmitting, setIsPasswordResetSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [activeScreen, setActiveScreen] = useState('main');

  useEffect(() => {
    initializeProfileMenu();
  }, []);

  const resetPasswordState = () => {
    setPasswordResetStartForm(EMPTY_PASSWORD_RESET_START_FORM);
    setPasswordResetConfirmForm(EMPTY_PASSWORD_RESET_CONFIRM_FORM);
    setPasswordResetStep('start');
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setError('');

    try {
      await signIn(loginForm);
      setLoginForm(EMPTY_LOGIN_FORM);
      setActiveScreen('main');
      showToast('Вы вошли в аккаунт.');
    } catch (submitError) {
      setError(submitError.message);
    }
  };

  const handleRegisterSubmit = async (event) => {
    event.preventDefault();
    setError('');

    try {
      await signUp(registerForm);
      setRegisterForm(EMPTY_REGISTER_FORM);
      setActiveScreen('main');
      showToast('Аккаунт создан и уже активен.');
    } catch (submitError) {
      setError(submitError.message);
    }
  };

  const handlePasswordResetStartSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsPasswordResetSubmitting(true);

    try {
      const loginOrEmail = passwordResetStartForm.loginOrEmail.trim();
      await startPasswordReset(loginOrEmail);
      setPasswordResetConfirmForm((current) => ({
        ...current,
        email: loginOrEmail.includes('@') ? loginOrEmail : current.email,
      }));
      setPasswordResetStep('confirm');
      showToast('Если для аккаунта доступно восстановление, код уже отправлен на почту.');
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsPasswordResetSubmitting(false);
    }
  };

  const handlePasswordResetConfirmSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsPasswordResetSubmitting(true);

    try {
      await confirmPasswordReset(
        passwordResetConfirmForm.email.trim(),
        passwordResetConfirmForm.code.trim(),
        passwordResetConfirmForm.newPassword
      );
      resetPasswordState();
      setActiveScreen('main');
      setAuthMode('login');
      showToast('Пароль обновлён. Теперь можно войти с новым паролем.');
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsPasswordResetSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setError('');
    await signOut();
    setAuthMode('login');
    setActiveScreen('main');
    showToast('Вы вышли из аккаунта.');
  };

  const openSettingsScreen = () => {
    setError('');
    setActiveScreen('settings');
  };

  const openPasswordResetScreen = () => {
    setError('');
    resetPasswordState();
    setActiveScreen('password-reset');
  };

  const closeSecondaryScreen = () => {
    setError('');
    resetPasswordState();
    setActiveScreen('main');
  };

  const renderSettingsScreen = () => (
    <div className="profile-settings-screen">
      <div className="settingsTitle profile-settings-header">
        <button
          type="button"
          className="back-btn"
          onClick={closeSecondaryScreen}
          aria-label="Назад"
        >
          <img className="backIcon" src={BACK_ICON_SRC} alt="" aria-hidden="true" />
        </button>
        <strong>Настройки</strong>
      </div>
      <ThemeControls />
      {isAuthenticated && (
        <a className="blue-btn profile-link-btn profile-settings-link" href="/settings">Все настройки</a>
      )}
    </div>
  );

  const renderPasswordResetScreen = () => (
    <div className="profile-settings-screen">
      <div className="settingsTitle profile-settings-header">
        <button
          type="button"
          className="back-btn"
          onClick={closeSecondaryScreen}
          aria-label="Назад"
        >
          <img className="backIcon" src={BACK_ICON_SRC} alt="" aria-hidden="true" />
        </button>
        <strong>Восстановление доступа</strong>
      </div>

      {passwordResetStep === 'start' ? (
        <form className="profile-auth-form" onSubmit={handlePasswordResetStartSubmit}>
          <p className="profile-helper-text">
            Введите логин или email. Если для аккаунта подключено восстановление, мы пришлём код на почту.
          </p>
          <input
            type="text"
            placeholder="Логин или email"
            autoComplete="username"
            value={passwordResetStartForm.loginOrEmail}
            onInput={(event) => setPasswordResetStartForm({
              loginOrEmail: event.target.value,
            })}
          />
          <button className="blue-btn" type="submit" disabled={isPasswordResetSubmitting}>
            {isPasswordResetSubmitting ? 'Отправляем...' : 'Отправить код'}
          </button>
        </form>
      ) : (
        <form className="profile-auth-form" onSubmit={handlePasswordResetConfirmSubmit}>
          <p className="profile-helper-text">
            Укажите почту, код из письма и новый пароль.
          </p>
          <input
            type="email"
            placeholder="Email"
            autoComplete="email"
            value={passwordResetConfirmForm.email}
            onInput={(event) => setPasswordResetConfirmForm((current) => ({
              ...current,
              email: event.target.value,
            }))}
          />
          <input
            type="text"
            placeholder="Код"
            autoComplete="one-time-code"
            value={passwordResetConfirmForm.code}
            onInput={(event) => setPasswordResetConfirmForm((current) => ({
              ...current,
              code: event.target.value,
            }))}
          />
          <input
            type="password"
            placeholder="Новый пароль"
            autoComplete="new-password"
            value={passwordResetConfirmForm.newPassword}
            onInput={(event) => setPasswordResetConfirmForm((current) => ({
              ...current,
              newPassword: event.target.value,
            }))}
          />
          <button className="blue-btn" type="submit" disabled={isPasswordResetSubmitting}>
            {isPasswordResetSubmitting ? 'Сохраняем...' : 'Сменить пароль'}
          </button>
          <button
            type="button"
            className="profile-inline-link"
            onClick={() => {
              setError('');
              setPasswordResetStep('start');
            }}
          >
            Отправить код заново
          </button>
        </form>
      )}
    </div>
  );

  return (
    <div
      className="profile-dropdown"
      onClick={(e) => e.stopPropagation()}
    >
      {activeScreen === 'settings' && renderSettingsScreen()}
      {activeScreen === 'password-reset' && renderPasswordResetScreen()}

      {isLoading && (
        <div className="profile-loading">Загружаем аккаунт...</div>
      )}

      {!isLoading && !isAuthenticated && activeScreen === 'main' && (
        <>
          <div className="profile-guest-header">
            <div className="username">Гость</div>
            <button
              type="button"
              className="profile-guest-settings-btn"
              onClick={openSettingsScreen}
              aria-label="Настройки"
              title="Настройки"
            >
              <img
                className="profile-guest-settings-icon"
                src={SETTINGS_ICON_SRC}
                alt=""
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="profile-auth-switch">
            <button
              className={`auth-switch-btn ${authMode === 'login' ? 'active' : ''}`}
              onClick={() => {
                setAuthMode('login');
                setError('');
              }}
            >
              Вход
            </button>
            <button
              className={`auth-switch-btn ${authMode === 'register' ? 'active' : ''}`}
              onClick={() => {
                setAuthMode('register');
                setError('');
              }}
            >
              Регистрация
            </button>
          </div>

          {authMode === 'login' ? (
            <form className="profile-auth-form" onSubmit={handleLoginSubmit}>
              <input
                type="text"
                placeholder="Логин"
                autoComplete="username"
                value={loginForm.login}
                onInput={(event) => setLoginForm((current) => ({
                  ...current,
                  login: event.target.value,
                }))}
              />
              <input
                type="password"
                placeholder="Пароль"
                autoComplete="current-password"
                value={loginForm.password}
                onInput={(event) => setLoginForm((current) => ({
                  ...current,
                  password: event.target.value,
                }))}
              />
              <button className="blue-btn" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Входим...' : 'Войти'}
              </button>
              <button
                type="button"
                className="profile-inline-link"
                onClick={openPasswordResetScreen}
              >
                Забыли пароль?
              </button>
            </form>
          ) : (
            <form className="profile-auth-form" onSubmit={handleRegisterSubmit}>
              <input
                type="text"
                placeholder="Логин"
                autoComplete="username"
                value={registerForm.login}
                onInput={(event) => setRegisterForm((current) => ({
                  ...current,
                  login: event.target.value,
                }))}
              />
              <input
                type="email"
                placeholder="Email"
                autoComplete="email"
                value={registerForm.email}
                onInput={(event) => setRegisterForm((current) => ({
                  ...current,
                  email: event.target.value,
                }))}
              />
              <input
                type="password"
                placeholder="Пароль"
                autoComplete="new-password"
                value={registerForm.password}
                onInput={(event) => setRegisterForm((current) => ({
                  ...current,
                  password: event.target.value,
                }))}
              />
              <button className="blue-btn" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Создаём...' : 'Создать аккаунт'}
              </button>
            </form>
          )}
        </>
      )}

      {!isLoading && isAuthenticated && activeScreen === 'main' && (
        <>
          <div className="username">@{user?.login || 'user'}</div>
          <div className="profile-meta">
            <span>{user?.email || 'Email пока не указан'}</span>
            <span>{user?.emailVerified ? 'Email подтверждён' : 'Email не подтверждён'}</span>
            {user?.permissions?.managePermissions && <span className="profile-role">Администратор</span>}
          </div>
          <div className="profile-action-group">
            <a className="blue-btn profile-link-btn" href="/account">Аккаунт</a>
            <button
              type="button"
              className="blue-btn profile-settings-entry"
              onClick={openSettingsScreen}
            >
              Настройки
            </button>
            <button className="red-btn" onClick={handleSignOut}>Выйти</button>
          </div>
        </>
      )}

      {error && <div className="profile-notice error">{error}</div>}
    </div>
  );
};

export default Profile;
